import { format, parseISO } from "date-fns";
import { FileSignature, Plus } from "lucide-react";
import { useTranslate, type Identifier } from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";

import { LoadMore } from "../../operations/LoadMore";
import { useInfiniteRecords } from "../../operations/useInfiniteRecords";
import { useDateLocale } from "../../personal/useDateLocale";
import type { FinanceContract, FinanceContractStatus } from "../../types";
import { FinanceListToolbar } from "../FinanceListToolbar";
import { parseView } from "../financeViews";
import { useCompanyNames } from "../useCompanyNames";
import { useFormatMoney } from "../useFormatMoney";
import { ContractDialog } from "./ContractDialog";

const CONTRACT_VIEWS = ["active", "paused", "cancelled", "all"] as const;
const SORT = { field: "start_date", order: "DESC" } as const;

const STATUS_VARIANTS: Record<
  FinanceContractStatus,
  "default" | "secondary" | "outline"
> = { active: "default", paused: "secondary", cancelled: "outline" };

const ContractStatusBadge = ({ status }: { status: FinanceContractStatus }) => {
  const translate = useTranslate();
  return (
    <Badge variant={STATUS_VARIANTS[status]}>
      {translate(`crm.finance.contracts.statuses.${status}`)}
    </Badge>
  );
};

/** Contracts: implementation fee + monthly fee per company */
export const ContractList = () => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const { dateFnsLocale } = useDateLocale();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"), CONTRACT_VIEWS);
  const q = searchParams.get("q") ?? "";
  const filter = useMemo(
    () => ({
      ...(view === "all" ? {} : { status: view }),
      ...(q ? { q } : {}),
    }),
    [view, q],
  );
  const {
    records: contracts,
    total,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteRecords<FinanceContract>("finance_contracts", filter, SORT);
  const companyNames = useCompanyNames(contracts);
  const [editing, setEditing] = useState<FinanceContract | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const companyName = (contract: FinanceContract) =>
    companyNames.get(contract.company_id as Identifier) ?? "";
  const setup = (contract: FinanceContract) =>
    contract.setup_installments > 1
      ? `${formatMoney(contract.setup_amount)} (${contract.setup_installments}x)`
      : formatMoney(contract.setup_amount);
  const day = (value: string) =>
    format(parseISO(value), "P", { locale: dateFnsLocale });

  return (
    <div className="flex flex-col gap-3">
      <FinanceListToolbar
        views={CONTRACT_VIEWS}
        view={view}
        labelPrefix="crm.finance.contracts.views"
        actions={
          <Button
            type="button"
            onClick={() => setIsCreating(true)}
            aria-label={translate("crm.finance.contracts.create")}
          >
            <Plus />
            <span className="hidden md:inline">
              {translate("crm.finance.contracts.create")}
            </span>
          </Button>
        }
      />
      {isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : total === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground">
          <FileSignature className="size-8" />
          {translate(
            view === "active" && !q
              ? "crm.finance.contracts.empty"
              : "crm.finance.empty_filtered",
          )}
        </div>
      ) : isMobile ? (
        <ul className="flex flex-col gap-2">
          {contracts.map((contract) => (
            <li
              key={contract.id}
              className="flex flex-col gap-1 rounded-lg border p-3"
              onClick={() => setEditing(contract)}
              data-testid="contract-row"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium">{contract.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {companyName(contract)}
                  </p>
                </div>
                <ContractStatusBadge status={contract.status} />
              </div>
              <p className="text-sm">
                {translate("crm.finance.contracts.per_month", {
                  amount: formatMoney(contract.monthly_amount),
                })}
                <span className="text-muted-foreground">
                  {" · "}
                  {translate("crm.finance.contracts.fields.setup_amount")}{" "}
                  {setup(contract)}
                </span>
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {translate("crm.finance.contracts.fields.name")}
                </TableHead>
                <TableHead>{translate("crm.finance.fields.company")}</TableHead>
                <TableHead className="text-right">
                  {translate("crm.finance.contracts.fields.monthly_amount")}
                </TableHead>
                <TableHead className="text-right">
                  {translate("crm.finance.contracts.fields.setup_amount")}
                </TableHead>
                <TableHead>
                  {translate("crm.finance.contracts.fields.billing_day")}
                </TableHead>
                <TableHead>
                  {translate("crm.finance.contracts.fields.start_date")}
                </TableHead>
                <TableHead>{translate("crm.finance.fields.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map((contract) => (
                <TableRow
                  key={contract.id}
                  className="cursor-pointer"
                  onClick={() => setEditing(contract)}
                  data-testid="contract-row"
                >
                  <TableCell className="max-w-56 truncate font-medium">
                    {contract.name}
                  </TableCell>
                  <TableCell className="max-w-48 truncate text-muted-foreground">
                    {companyName(contract)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {formatMoney(contract.monthly_amount)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {setup(contract)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {contract.billing_day}
                  </TableCell>
                  <TableCell className="text-xs">
                    {day(contract.start_date)}
                    {contract.end_date && ` → ${day(contract.end_date)}`}
                  </TableCell>
                  <TableCell>
                    <ContractStatusBadge status={contract.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <LoadMore
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      />
      <ContractDialog
        open={isCreating || editing != null}
        contract={editing}
        onClose={() => {
          setIsCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
};
